import { pantryColors } from '@pantry/ui'
import type {
  AffinityLevel,
  BehaviorAffinityRow,
  BehaviorProductRow,
} from '../../../gateways/breadstack/customer/behaviorProductsApis'
import type { PillTone } from '../components/profile/tabs/behavior/v2/shared/theme'
import type { ConicSlice } from './behaviorEngagement'

// Pure display helpers for the Behavior › Products sub-tab. Free of React so
// edge cases are unit-tested.

const C = pantryColors

// Avg product view time: 72 → `1m 12s`, 45 → `45s`, 0 / invalid → `—`.
// Separate from `formatDuration` (always `Xm Ys`), which other tabs use.
export const formatViewTime = (seconds: number | null | undefined): string => {
  const s =
    typeof seconds === 'number' && Number.isFinite(seconds)
      ? Math.round(seconds)
      : 0
  if (s <= 0) return '—'
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

// Name fallback when the catalog name is blank.
export const productName = (
  row: Pick<BehaviorProductRow, 'name' | 'productId'>,
) => row.name || `Product #${row.productId}`

// Activity-table count pill: the column's tone, gray when there's nothing
// (≤ 0 — purchases are net of refunds and may go negative).
export const countPillTone = (value: number, tone: PillTone): PillTone =>
  Number.isFinite(value) && value > 0 ? tone : 'gray'

// Display clamp for net-of-refund values (row stays visible).
export const clampNonNeg = (value: number): number =>
  Number.isFinite(value) && value > 0 ? value : 0

export const AFFINITY_LEVEL: Record<
  AffinityLevel,
  { label: string; bar: string; text: string }
> = {
  high: { label: 'High', bar: '#16A34A', text: '#16A34A' },
  medium: { label: 'Med', bar: C.blue[700], text: C.blue[700] },
  low: { label: 'Low', bar: C.amber[700], text: C.amber[700] },
  none: { label: 'None', bar: C.gray[300], text: C.gray[400] },
}

// Affinity bar width (%): `none` keeps a visible stub (min 15%).
export const affinityBarWidth = (
  score: number,
  level: AffinityLevel,
): number => {
  const s = Number.isFinite(score) ? Math.min(100, Math.max(0, score)) : 0
  return level === 'none' ? Math.max(s, 15) : s
}

// Mockup pie palette, by rank (green → gray).
export const AFFINITY_PIE_COLORS = [
  '#16A34A',
  '#22C55E',
  '#1D4ED8',
  '#B45309',
  '#8B5CF6',
  '#D1D5DB',
]

export const affinityColor = (index: number): string =>
  AFFINITY_PIE_COLORS[Math.min(index, AFFINITY_PIE_COLORS.length - 1)]

// Pie slices for items with revenue share > 0, coloured by their rank in the
// full item list (so legend dots and slices agree).
export const affinitySlices = (items: BehaviorAffinityRow[]): ConicSlice[] =>
  items
    .map((it, i) => ({ color: affinityColor(i), percent: it.revenueShare }))
    .filter((s) => Number.isFinite(s.percent) && s.percent > 0)
